import React, { useCallback, useEffect, useState } from 'react';

import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { useIsMounted } from '../../../lib/hooks/use-is-mounted';
import { ICommunicationVariable } from '../../../lib/types/communication.types';
import { keepFieldFocusOnMouseDown } from '../ComposePanel/compose-panel.insert';
import { buildVariableToken } from './variable-panel.helpers';
import { getMessages } from './variable-panel.messages';
import { TokenChipButton, VariableLabel, VariableRowLine } from './variable-panel.styles';

interface IVariableRowProps {
  variable: ICommunicationVariable;
  // Fired on chip click with the chip element, so the panel can anchor the
  // fallback prompt for requiresFallback variables.
  onInsertClick: (variable: ICommunicationVariable, anchorEl: HTMLElement) => void;
}

const COPY_STATUS_RESET_MS = 2500;

export const VariableRow: React.FC<IVariableRowProps> = ({ variable, onInsertClick }) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  // Clipboard promises can settle after the panel collapses (unmountOnExit) —
  // guard the setState per REACT_CONVENTIONS "Guard setState in async continuations".
  const isMounted = useIsMounted();

  useEffect(() => {
    if (copyStatus === 'idle') return undefined;
    const timer = window.setTimeout(() => setCopyStatus('idle'), COPY_STATUS_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  const handleCopy = useCallback(() => {
    const token = buildVariableToken(variable.key);
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      console.error(`copy variable ${variable.key} failed: clipboard API unavailable`);
      setCopyStatus('failed');
      return;
    }
    navigator.clipboard.writeText(token)
      .then(() => { if (isMounted()) setCopyStatus('copied'); })
      .catch((error) => {
        console.error(`copy variable ${variable.key} failed:`, error);
        if (isMounted()) setCopyStatus('failed');
      });
  }, [variable.key]);

  const tooltip = (
    <>
      {variable.example && (
        <div><IntlMsg {...messages.exampleTooltip} values={{ example: variable.example }} /></div>
      )}
      {variable.requiresFallback && (
        <div><IntlMsg {...messages.requiresFallbackTooltip} /></div>
      )}
    </>
  );

  return (
    <VariableRowLine>
      <Tooltip title={tooltip} arrow>
        <TokenChipButton
          type="button"
          onMouseDown={keepFieldFocusOnMouseDown}
          onClick={(event) => onInsertClick(variable, event.currentTarget)}
        >
          {buildVariableToken(variable.key)}
        </TokenChipButton>
      </Tooltip>
      <VariableLabel>{variable.label}</VariableLabel>
      {copyStatus !== 'idle' && (
        <Typography variant="caption" color={copyStatus === 'failed' ? 'error' : 'textSecondary'}>
          <IntlMsg {...(copyStatus === 'failed' ? messages.copyFailed : messages.copied)} />
        </Typography>
      )}
      <IconButton
        size="small"
        aria-label={`Copy ${variable.key}`}
        onMouseDown={keepFieldFocusOnMouseDown}
        onClick={handleCopy}
      >
        {copyStatus === 'copied'
          ? <CheckIcon style={{ fontSize: 14 }} />
          : <FileCopyIcon style={{ fontSize: 14 }} />}
      </IconButton>
    </VariableRowLine>
  );
};
